import i18next from 'i18next'
import { List, Set } from 'immutable'
import { Action, AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { PlannedStopFormProps } from '../../components/dispatchModal/DispatchModal'
import { Break } from '../../components/unit/UnitForm'
import { api } from '../../http/httpHelper'
import { DEFAULT_SELECT_COLUMN } from '../../pages/instant/InstantGrid'
import { OrderCorrectionDetailProps, OrderCorrectionType } from '../../pages/instant/orderCorrectionTypes'
import {
  Period,
  RecurringOrderExceptionProps,
  RecurringOrderProps,
  RecurringOrderType,
  TemplateException
} from '../../pages/planner/recurring/recurringOrderTypes'
import { RoutePointOrder } from '../../query/instantCourierQuery'
import {
  ClearAllFiltersAction,
  ClearFilterAction,
  ClearOptimizeJobResultAction,
  HidePopoverAction,
  SetFilterAction,
  TogglePopoverAction
} from '../../reducers/filterReducer'
import { ConsignmentIdType, Entity, OrderIdType, SlotIdType } from '../../types/coreEntitiesTypes'
import { ImmutableMap } from '../../types/immutableTypes'
import { AppStateType } from '../../utils/appStateReduxStore'
import { OptimizingType } from '../../utils/optimizingUtils'
import {
  ACCEPT_ORDER_CORRECTION,
  ADD_RECURRING_ORDER_EXCEPTIONS,
  ADD_TEMPLATE_ROUTE_EXCEPTIONS,
  APPLY_SUGGESTED_ROUTE,
  CANCEL_SUGGESTED_ROUTE,
  CLEAR_ALL_COLUMN_FILTERS,
  CLEAR_COLUMN_FILTER,
  CLEAR_OPTIMIZE_RESULTS,
  CREATE_OR_UPDATE_RECURRING_ORDER,
  CREATE_OR_UPDATE_RECURRING_ORDERS,
  CREATE_SUGGESTED_ROUTE,
  CREATE_SUGGESTED_ROUTE_WITH_CONSIGNMENTS,
  DELETE_PLANNED_STOP_FROM_SLOT,
  DELETE_RECURRING_ORDER,
  DELETE_RECURRING_ORDER_EXCEPTIONS,
  DELETE_TEMPLATE_ROUTE_EXCEPTIONS,
  DELETE_UNASSIGNED_PLANNED_STOP_ROUTE_POINT_SUGGESTION,
  DELETE_UNASSIGNED_ROUTE_POINT_SUGGESTIONS,
  EXECUTE_RECURRING_ORDERS,
  GET_RECURRING_EXECUTED_DATES,
  GET_RECURRING_ORDERS,
  GET_RECURRING_ORDERS_FOR_DEPARTMENTS,
  GET_RECURRING_ROUTE,
  GET_RECURRING_ROUTES,
  GET_RECURRING_ROUTES_SHALLOW,
  GRID_DISABLE_ROWS,
  GRID_INIT,
  GRID_INIT_SORT,
  GRID_RESET_SORT,
  GRID_SELECT_NONE,
  GRID_SELECT_UPDATE,
  GRID_SORT_BY_COLUMN,
  GRID_SORT_DESCENDING,
  GRID_TOGGLE_GROUP_EXPANDED,
  GROUP_ORDERS,
  HIDE_POPOVER,
  PREPLAN_CONSIGNMENTS_TO_COURIER,
  REJECT_ORDER_CORRECTION,
  REORDER_SUGGESTED_ROUTE,
  SET_COLUMN_FILTER,
  TOGGLE_POPOVER,
  TOGGLE_SLOT_MANUAL_PLANNING,
  UNGROUP_ORDERS,
  UPSERT_PLANNED_STOP
} from '../actionTypes'
import { fetchEvenIfPresent, fetchIfNotPresent } from './baseHelpers'
import { closeModalAndNotify } from './helpers'

export interface ForcedOrderingProps {
  id: number
  type: 'PICKUP' | 'DELIVERY' | 'PLANNED_STOP'
}

export interface OptimizeSlotProps {
  slot: SlotIdType
  orders: Set<OrderIdType>
  consignments: Set<ConsignmentIdType>
  flexPickups: Set<ConsignmentIdType>
  flexDeliveries: Set<ConsignmentIdType>
  forcedOrderPickups: Set<ConsignmentIdType>
  forcedOrderDeliveries: Set<ConsignmentIdType>
  forcedOrdering?: ForcedOrderingProps[]
  flexPlannedStops: number[]
  forcedPlannedStops: number[]
  usePrevious?: boolean
  returnRoutePointSuggestions?: boolean
  optimizeOnRoutePointSuggestionsOnly: boolean
  break?: Break
  ignoreCourierBreak?: boolean
  applySuggestedRoute?: boolean
  optimizingType: OptimizingType
  plannedStopsToBeExcluded?: number[]
  ignorePalletRule?: boolean
}

export interface OptimizeSlotPayload extends ImmutableMap<OptimizeSlotProps> {}

export const createSuggestedRouteWithExtraConsignments =
  (slotId: SlotIdType, consignmentIds: Set<ConsignmentIdType>, allowSortingErrors = false, takeOver = false) =>
  (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch(
      api.asyncCommand(CREATE_SUGGESTED_ROUTE_WITH_CONSIGNMENTS, {
        type: 'CREATE_SUGGESTED_ROUTE_WITH_CONSIGNMENTS',
        payload: {
          slot: slotId,
          consignments: consignmentIds,
          allowSortingErrors,
          ...(takeOver && { takeOver: true })
        }
      })
    )
  }

export const routePointsToSuggestedRoute =
  (slotId: SlotIdType, takeOver: boolean) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch(
      api.asyncCommand(CREATE_SUGGESTED_ROUTE, {
        type: 'CREATE_SUGGESTED_ROUTE',
        payload: {
          slot: slotId,
          ...(takeOver && { takeOver: true })
        }
      })
    )
  }

export const reorderSuggestedRoute =
  (slotId: SlotIdType, routePoints: RoutePointOrder[]) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch(
      api.asyncCommand(REORDER_SUGGESTED_ROUTE, {
        type: 'REORDER_SUGGESTED_ROUTE',
        payload: {
          slot: slotId,
          route: routePoints
        }
      })
    )
  }

export const upsertPlannedStopRouteSuggestion =
  (slotId: SlotIdType, plannedStop: PlannedStopFormProps) =>
  (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch(
      api.asyncCommand(UPSERT_PLANNED_STOP, {
        type: UPSERT_PLANNED_STOP.name,
        payload: {
          slotId,
          plannedStop
        }
      })
    )
  }

export const applySuggestedRouteToSlotRequest =
  (
    slotId: SlotIdType,
    consignmentIdsToUnassign: Set<ConsignmentIdType>,
    assignInsteadOfOffer?: boolean,
    updatedBreak?: Break,
    deleteBreak?: boolean,
    plannedStopsToBeDeleted?: number[],
    partiallyForced?: boolean,
    palletRuleOverridden?: boolean
  ) =>
  (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch(
      api.asyncCommand(APPLY_SUGGESTED_ROUTE, {
        type: 'APPLY_SUGGESTED_ROUTE',
        payload: {
          slot: slotId,
          assignToSlot: assignInsteadOfOffer,
          consignmentIdsToUnassign,
          updatedBreak,
          deleteBreak,
          plannedStopsToBeDeleted,
          partiallyForced,
          palletRuleOverridden
        }
      })
    )
  }

export const cancelSuggestedRoute =
  (slotId: SlotIdType, closeAndNotify = true) =>
  (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    dispatch(
      api.asyncCommand(CANCEL_SUGGESTED_ROUTE, {
        type: 'CANCEL_SUGGESTED_ROUTE',
        payload: {
          slot: slotId
        }
      })
    ).then(
      (res: Response) =>
        res.ok && closeAndNotify
          ? dispatch(closeModalAndNotify(i18next.t('instant.suggestedRouteCancelled'), '', 3000, true))
          : res,
      (err: any) => console.log('cancelSuggestedRoute', err)
    )
  }

export const deleteUnassignedRoutePointSuggestions =
  (consignmentIds: Set<number>, callback: () => void) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) =>
    dispatch(
      api.asyncCommand(DELETE_UNASSIGNED_ROUTE_POINT_SUGGESTIONS, {
        type: DELETE_UNASSIGNED_ROUTE_POINT_SUGGESTIONS.name,
        payload: { consignments: consignmentIds }
      })
    ).then(
      (res: Response) => (res.ok && callback ? callback() : res),
      (err: any) => console.log('deleteUnassignedRoutePointSuggestions', err)
    )

export const deleteUnassignedPlannedStopRoutePointSuggestion =
  (stopId: number) => (dispatch: ThunkDispatch<AppStateType, null, Action>) =>
    dispatch(
      api.asyncCommand(DELETE_UNASSIGNED_PLANNED_STOP_ROUTE_POINT_SUGGESTION, {
        type: DELETE_UNASSIGNED_PLANNED_STOP_ROUTE_POINT_SUGGESTION.name,
        payload: { stopId }
      })
    )

export const deletePlannedStopFromSlot = (stopId: number) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) =>
  dispatch(
    api.asyncCommand(DELETE_PLANNED_STOP_FROM_SLOT, {
      type: DELETE_PLANNED_STOP_FROM_SLOT.name,
      payload: { stopId }
    })
  )

export const toggleManuallyRoutePlanForSlot =
  (slotId: SlotIdType) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch(
      api.asyncCommand(TOGGLE_SLOT_MANUAL_PLANNING, { type: 'TOGGLE_SLOT_MANUAL_PLANNING', payload: { slot: slotId } })
    )
  }

export const gridInit = (key: string) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
  return dispatch({
    type: GRID_INIT,
    id: key
  })
}

export const gridSelect =
  (key: string, ids: Set<number>, selectAll: boolean, subKey: string = DEFAULT_SELECT_COLUMN) =>
  (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch({
      type: GRID_SELECT_UPDATE,
      id: key,
      column: subKey,
      selectedIds: ids,
      allSelected: selectAll
    })
  }

export const gridSelectNone =
  (key: string, subKey: string = DEFAULT_SELECT_COLUMN) =>
  (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch({
      type: GRID_SELECT_NONE,
      id: key,
      column: subKey
    })
  }

export const gridSortDescending =
  (key: string, sortDescending: boolean) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch({
      type: GRID_SORT_DESCENDING,
      id: key,
      sortDescending
    })
  }

export const gridSetSortColumnKey =
  (key: string, columnKey: string | undefined) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch({
      type: GRID_SORT_BY_COLUMN,
      id: key,
      sortedColumnKey: columnKey
    })
  }

export const gridResetSort = (key: string) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
  return dispatch({
    type: GRID_RESET_SORT,
    id: key
  })
}

export const gridInitSort =
  <T>(
    key: string,
    sortedColumnKey: keyof T,
    sortDescending: boolean,
    sortFunction: (d1: Entity<T>, d2: Entity<T>) => number
  ) =>
  (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch({
      type: GRID_INIT_SORT,
      id: key,
      sortedColumnKey,
      sortDescending,
      sortFunction
    })
  }

export const gridToggleGroupExpanded =
  (key: string, groupKey: string) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch({
      type: GRID_TOGGLE_GROUP_EXPANDED,
      id: key,
      groupKey
    })
  }

export const gridDisableRows =
  (key: string, disabledIds: Set<number>) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch({
      type: GRID_DISABLE_ROWS,
      id: key,
      disabledIds
    })
  }

export const rejectOrderCorrection =
  (orderId: OrderIdType, orderCorrectionId: number) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    dispatch(
      api.asyncCommand(REJECT_ORDER_CORRECTION, {
        type: 'REJECT_ORDER_CORRECTION',
        payload: {
          orderId,
          id: orderCorrectionId
        }
      })
    ).then(
      (res: Response) =>
        res.ok && dispatch(closeModalAndNotify(i18next.t('instant.corrections.rejected'), '', 1000, true)),
      (err: any) => console.log('rejectOrderCorrection', err)
    )
  }

export const acceptOrderCorrection =
  (values: OrderCorrectionType, orderCorrection: ImmutableMap<OrderCorrectionDetailProps>) =>
  (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    dispatch(
      api.asyncCommand(ACCEPT_ORDER_CORRECTION, {
        type: 'ACCEPT_ORDER_CORRECTION',
        payload: {
          orderId: orderCorrection.get('orderId'),
          id: orderCorrection.get('id'),
          numberOfPackages: values.get('numberOfPackages') || orderCorrection.get('existingPackages') || null,
          volumeDmq: values.get('volumeValue') || null,
          weightKilograms: values.get('weightValue') || null,
          deliveryWaitingTimeInMinutes: values.get('deliveryWaitingTimeInMinutes') || null,
          pickupWaitingTimeInMinutes: values.get('pickupWaitingTimeInMinutes') || null,
          deliveryLoadingTimeInMinutes: values.get('deliveryLoadingTimeInMinutes') || null,
          pickupLoadingTimeInMinutes: values.get('pickupLoadingTimeInMinutes') || null,
          comment: values.get('comment') || null
        }
      })
    ).then(
      (res: Response) =>
        res.ok && dispatch(closeModalAndNotify(i18next.t('instant.corrections.accepted'), '', 1000, true)),
      (err: any) => console.log('acceptOrderCorrection', err)
    )
  }

export const groupOrders =
  (groupId: string, groupName: string, orderIds: List<number>, fixedOrder: boolean, paidTogether: boolean) =>
  (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    const payload = {
      groupId,
      groupName,
      orderIds,
      fixedOrder,
      paidTogether
    }
    return dispatch(
      api.asyncCommand(GROUP_ORDERS, {
        type: GROUP_ORDERS.name,
        payload
      })
    )
  }

export const preplanConsignmentsToCourier =
  (courierId: string | null, consignments: Set<number>) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
    return dispatch(
      api.asyncCommand(PREPLAN_CONSIGNMENTS_TO_COURIER, {
        type: PREPLAN_CONSIGNMENTS_TO_COURIER.name,
        payload: {
          alystraId: courierId,
          consignments
        }
      })
    )
  }

export const ungroupOrders = (orderIds: List<number>) => (dispatch: ThunkDispatch<AppStateType, null, AnyAction>) => {
  const payload = {
    orderIds
  }
  return dispatch(
    api.asyncCommand(UNGROUP_ORDERS, {
      type: UNGROUP_ORDERS.name,
      payload
    })
  )
}

export const showPopover =
  (gridId: string, column: string) => (dispatch: ThunkDispatch<AppStateType, null, TogglePopoverAction>) => {
    return dispatch({
      type: TOGGLE_POPOVER,
      gridId,
      column
    })
  }

export const hidePopover =
  (gridId: string, column: string) => (dispatch: ThunkDispatch<AppStateType, null, HidePopoverAction>) => {
    return dispatch({
      type: HIDE_POPOVER,
      gridId,
      column
    })
  }

export const setFilter =
  (gridId: string, column: string, filter: string) =>
  (dispatch: ThunkDispatch<AppStateType, null, SetFilterAction>) => {
    return dispatch({
      type: SET_COLUMN_FILTER,
      gridId,
      column,
      filter
    })
  }

export const clearFilter =
  (gridId: string, column: string) => (dispatch: ThunkDispatch<AppStateType, null, ClearFilterAction>) => {
    return dispatch({
      type: CLEAR_COLUMN_FILTER,
      gridId,
      column
    })
  }

export const clearAllFilters =
  (gridId: string) => (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    return dispatch({
      type: CLEAR_ALL_COLUMN_FILTERS,
      gridId
    })
  }

export const addRecurringOrderExceptions =
  (recurringOrderId: number, orderIds: List<number>, exceptions: RecurringOrderExceptionProps[]) =>
  (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    const periods = exceptions.map(
      (ex) =>
        ({
          startDate: ex.startDate === '' ? null : ex.startDate,
          endDate: ex.endDate === '' ? null : ex.endDate,
          week: {
            monday: ex.monday,
            tuesday: ex.tuesday,
            wednesday: ex.wednesday,
            thursday: ex.thursday,
            friday: ex.friday,
            saturday: ex.saturday,
            sunday: ex.sunday
          }
        }) as Period
    )
    return dispatch(
      api.asyncCommand(ADD_RECURRING_ORDER_EXCEPTIONS, {
        type: ADD_RECURRING_ORDER_EXCEPTIONS.name,
        payload: { recurringOrderId, orderIds, periods }
      })
    )
  }

export const addRecurringTemplateExceptions =
  (
    templateRouteId: number,
    templateShipmentIds: List<number>,
    templatePlannedStopIds: List<number>,
    exceptions: TemplateException[]
  ) =>
  (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    const periods = exceptions.map((ex) => ({
      ...ex,
      startDate: ex.startDate === '' ? null : ex.startDate,
      endDate: ex.endDate === '' ? null : ex.endDate
    }))
    return dispatch(
      api.asyncCommand(ADD_TEMPLATE_ROUTE_EXCEPTIONS, {
        type: ADD_TEMPLATE_ROUTE_EXCEPTIONS.name,
        payload: { templateRouteId, templateShipmentIds, templatePlannedStopIds, periods }
      })
    )
  }

export const deleteRecurringOrderExceptions =
  (ids: number[]) => (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    return dispatch(
      api.asyncCommand(DELETE_RECURRING_ORDER_EXCEPTIONS, {
        type: DELETE_RECURRING_ORDER_EXCEPTIONS.name,
        payload: { ids }
      })
    )
  }

export const deleteRecurringTemplateExceptions =
  (ids: number[]) => (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    return dispatch(
      api.asyncCommand(DELETE_TEMPLATE_ROUTE_EXCEPTIONS, {
        type: DELETE_TEMPLATE_ROUTE_EXCEPTIONS.name,
        payload: { ids }
      })
    )
  }

export const createOrUpdateRecurringOrder =
  (order: RecurringOrderType) => (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    return dispatch(
      api.asyncCommand(CREATE_OR_UPDATE_RECURRING_ORDER, {
        type: CREATE_OR_UPDATE_RECURRING_ORDER.name,
        payload: order.toJS()
      })
    )
  }

export const createOrUpdateRecurringOrder2 =
  (order: RecurringOrderProps) => (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    return dispatch(
      api.asyncCommand(CREATE_OR_UPDATE_RECURRING_ORDER, {
        type: CREATE_OR_UPDATE_RECURRING_ORDER.name,
        payload: order
      })
    )
  }

export const createOrUpdateRecurringOrders =
  (recurringOrderIds: number[], orderIds: number[]) =>
  (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    return dispatch(
      api.asyncCommand(CREATE_OR_UPDATE_RECURRING_ORDERS, {
        type: CREATE_OR_UPDATE_RECURRING_ORDERS.name,
        payload: { recurringOrderIds, orderIds }
      })
    )
  }

export function deleteRecurringOrder(id: number) {
  return (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    return dispatch(api.asyncDeleteCommand(DELETE_RECURRING_ORDER, { type: 'DELETE_RECURRING_ORDER', payload: { id } }))
  }
}

export function getRecurringOrders(departmentId: number, force?: boolean) {
  const params = { department: departmentId }

  return force
    ? fetchEvenIfPresent(GET_RECURRING_ORDERS, { query: 'recurringOrders', params })
    : fetchIfNotPresent(GET_RECURRING_ORDERS, { query: 'recurringOrders', params })
}
export function getRecurringRoutes(departmentId: number, force?: boolean) {
  const params = { departmentIds: [departmentId] }

  return force
    ? fetchEvenIfPresent(GET_RECURRING_ROUTES, { query: 'recurringRoutes', params })
    : fetchIfNotPresent(GET_RECURRING_ROUTES, { query: 'recurringRoutes', params })
}

export function getRecurringRoutesShallow(departmentId: number) {
  return fetchIfNotPresent(GET_RECURRING_ROUTES_SHALLOW, {
    query: 'recurringRoutesShallow',
    params: { departmentIds: [departmentId] }
  })
}

export function getRecurringRoute(templateRouteId: number) {
  return fetchIfNotPresent(GET_RECURRING_ROUTE, { query: 'recurringRoute', params: { templateRouteId } })
}

export function getRecurringOrdersForDepartments(departmentIds: number[]) {
  const params = { departmentIds }

  return fetchIfNotPresent(GET_RECURRING_ORDERS_FOR_DEPARTMENTS, { query: 'recurringOrdersForDepartmentIds', params })
}

export function executeRecurringOrders(date: string, departmentId: number) {
  return (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    return dispatch(
      api.asyncCommand(EXECUTE_RECURRING_ORDERS, {
        type: 'EXECUTE_RECURRING_ORDERS',
        payload: {
          date,
          department: departmentId
        }
      })
    )
  }
}

export const resetOptimizeResults =
  () => (dispatch: ThunkDispatch<AppStateType, null, ClearOptimizeJobResultAction>) => {
    return dispatch({
      type: CLEAR_OPTIMIZE_RESULTS
    })
  }

export function getExecutedDates(departmentId: number) {
  const params = { department: departmentId }

  return (dispatch: ThunkDispatch<AppStateType, null, ClearAllFiltersAction>) => {
    return dispatch(api.asyncQuery(GET_RECURRING_EXECUTED_DATES, { query: 'getRecurringExecutedDates', params }))
  }
}
